import type {
  MatchState,
  MatchEvent,
  MatchEventType,
  Side,
  DuelTeam,
  FormationId,
  DuelPlayer,
} from "../types/leagueDuel";
import type { EquipmentStats } from "./combat";
// ─── Simulation Duel — Beta v1.1 ─────────────────────────────────────────────
// Each stat now has a specific role in phase resolution:
//   vitesse    → initiative (who starts each phase with the ball)
//   defense    → stopping the attack before a shot
//   technique  → extending phases (creative pass or counter-attack, max 1/phase)
//   physique   → shot power vs goalkeeper's defense → goal chance
//
// Critical events (Technique proc) are split across two ticks so that the
// Critical event is always events[0] in tick 1, triggering the portrait flash,
// and the shot resolves as events[0] in tick 2 via `pendingShot`.
//
// TODO Beta v1.2 : apply formation bonus/malus (3-1 / 2-2 / 1-3).
// TODO Beta v1.2 : apply spells.

const TOTAL_PHASES = 24;
const DEFAULT_ADVANTAGE = 0.5;

// ── Tuning constants ──────────────────────────────────────────────────────────
const SPEED_EXPONENT        = 1.0;
const SPEED_MIN_CHANCE      = 0.25;   // slowest team's floor initiative chance
const SPEED_MAX_CHANCE      = 0.75;   // fastest team's ceiling initiative chance

const TECHNIQUE_MIN_CHANCE  = 0.05;
const TECHNIQUE_BASE_CHANCE = 0.10;   // proc chance at equal technique (documents intent)
const TECHNIQUE_MAX_CHANCE  = 0.30;

const GOAL_MIN_CHANCE       = 0.05;
const GOAL_MAX_CHANCE       = 0.85;
const GOAL_EXPONENT         = 0.5;

const DEFENSE_BASE_STOP     = 0.20;   // stop chance at equal defense / technique
const DEFENSE_MIN_STOP      = 0.10;
const DEFENSE_MAX_STOP      = 0.35;
// ─────────────────────────────────────────────────────────────────────────────

const BASE_STATS: EquipmentStats = { technique: 10, defense: 10, physique: 10, vitesse: 10 };

let EVENT_SEQ = 0;
const nextEventId = () => ++EVENT_SEQ;

const other = (s: Side): Side => (s === "home" ? "away" : "home");
const teamOf = (state: MatchState, s: Side): DuelTeam => (s === "home" ? state.home : state.away);
const pick = <T,>(arr: T[], rng: () => number): T => arr[Math.floor(rng() * arr.length)];

function buildEvent(
  phase: number,
  type: MatchEventType,
  side: Side,
  player: DuelPlayer,
  label: string,
  floatingLabel: string,
  tone?: "critical"
): MatchEvent {
  return { id: nextEventId(), phase, type, side, playerId: player.id, playerName: player.name, label, floatingLabel, tone };
}

/**
 * Technique proc chance [TECHNIQUE_MIN..TECHNIQUE_MAX].
 * At equal stats → TECHNIQUE_BASE_CHANCE (0.10).
 * Formula: 0.60 * (own / total) - 0.20, clamped.
 */
function computeTechChance(own: number, opp: number): number {
  const total = Math.max(1, own + opp);
  return Math.max(TECHNIQUE_MIN_CHANCE, Math.min(TECHNIQUE_MAX_CHANCE, 0.60 * (own / total) - 0.20));
}

/**
 * Physique vs Defense → goal chance, per spec.
 * Equal → 35%. Physique dominant → up to 85%. Defense dominant → down to 5%.
 */
function computeGoalChance(physique: number, defense: number): number {
  const phys = Math.max(1, physique);
  const def  = Math.max(1, defense);
  let chance: number;
  if (phys >= def) {
    chance = GOAL_MAX_CHANCE - (GOAL_MAX_CHANCE - 0.35) * (def / phys) ** GOAL_EXPONENT;
  } else {
    chance = GOAL_MIN_CHANCE + (0.35 - GOAL_MIN_CHANCE) * (phys / def) ** GOAL_EXPONENT;
  }
  return Math.max(GOAL_MIN_CHANCE, Math.min(GOAL_MAX_CHANCE, chance));
}

/** Creates the initial match state, ready to step. */
export function createInitialMatch(
  home: DuelTeam,
  away: DuelTeam,
  formationHome: FormationId,
  formationAway: FormationId,
  homeAdvantage: number = DEFAULT_ADVANTAGE,
  homeStats: EquipmentStats = BASE_STATS,
  awayStats: EquipmentStats = BASE_STATS,
  rng: () => number = Math.random
): MatchState {
  const startSide: Side = rng() < 0.5 ? "home" : "away";
  const startTeam = startSide === "home" ? home : away;
  const holder = startTeam.players[0];

  const fatigue: Record<string, number> = {};
  [...home.players, ...away.players].forEach((p) => (fatigue[p.id] = 0));

  const kickoff: MatchEvent = {
    id: nextEventId(),
    phase: 0,
    type: "kickoff",
    side: startSide,
    playerId: holder.id,
    playerName: holder.name,
    label: `Kick-off — ${startTeam.name} starts.`,
    floatingLabel: "⚽ Kick-off",
  };

  return {
    home,
    away,
    formationHome,
    formationAway,
    phase: 1,
    totalPhases: TOTAL_PHASES,
    possession: startSide,
    ball: { side: startSide, playerId: holder.id },
    homeScore: 0,
    awayScore: 0,
    homeAdvantage,
    homeStats,
    awayStats,
    fatigue,
    events: [kickoff],
    finished: false,
  };
}

/** Advances the match by one phase (or resolves a pending shot) and returns a new immutable state. */
export function stepMatch(state: MatchState, rng: () => number = Math.random): MatchState {
  if (state.finished) return state;

  // ── Pending shot continuation ─────────────────────────────────────────────
  // A Critical event was shown alone last tick (events[0] = critical → portrait flash fired).
  // Now resolve the actual shot for that critical action.
  if (state.pendingShot) {
    const { atkSide, shooter } = state.pendingShot;
    const defSide  = other(atkSide);
    const atkStats = atkSide === "home" ? state.homeStats : state.awayStats;
    const defStats = defSide === "home" ? state.homeStats : state.awayStats;
    const atkTeam  = teamOf(state, atkSide);
    const defTeam  = teamOf(state, defSide);

    // Recover the full player object from the team roster (avatar, isCaptain, etc.)
    const shooterPlayer: DuelPlayer =
      atkTeam.players.find((p) => p.id === shooter.id) ??
      { id: shooter.id, name: shooter.name, avatar: "" };

    const newEvents: MatchEvent[] = [];
    let homeScore = state.homeScore;
    let awayScore = state.awayScore;

    const atkPhys    = Math.max(1, atkStats.physique);
    const defDefStat = Math.max(1, defStats.defense);
    const goalChance = computeGoalChance(atkPhys, defDefStat);
    const goalRoll   = rng();

    console.log(
      `[Phase ${state.phase}] PendingShot (${atkSide}): phys=${atkPhys} vs def=${defDefStat}, goalChance=${goalChance.toFixed(2)}, roll=${goalRoll.toFixed(3)} → ${goalRoll < goalChance ? "GOAL" : "no goal"}`
    );

    newEvents.push(
      buildEvent(state.phase, "shot", atkSide, shooterPlayer,
        `${shooter.name} shoots!`, "🎯 Shot")
    );

    if (goalRoll < goalChance) {
      if (atkSide === "home") homeScore++; else awayScore++;
      newEvents.push(
        buildEvent(state.phase, "goal", atkSide, shooterPlayer,
          `⚽ GOAL by ${shooter.name}!`, "🥅 GOAL!")
      );
    } else {
      const gk = pick(defTeam.players, rng);
      if (rng() < 0.6) {
        newEvents.push(
          buildEvent(state.phase, "save", defSide, gk,
            `${gk.name} makes the save.`, "🧤 Save")
        );
      } else {
        newEvents.push(
          buildEvent(state.phase, "miss", atkSide, shooterPlayer,
            `${shooter.name} misses the target.`, "😖 Miss")
        );
      }
    }

    // After any shot, possession transfers to the team that did NOT shoot.
    const newPossession = defSide;
    const newBallHolder = pick(teamOf(state, newPossession).players, rng).id;

    const fatigue = { ...state.fatigue };
    fatigue[shooter.id] = Math.min(100, (fatigue[shooter.id] ?? 0) + 6);

    const nextPhase           = state.phase + 1;
    const isGoldenGoal        = state.goldenGoal ?? false;
    const goalScoredThisPhase = homeScore !== state.homeScore || awayScore !== state.awayScore;

    let finished      = false;
    let newGoldenGoal = isGoldenGoal;

    if (isGoldenGoal) {
      finished = goalScoredThisPhase;
    } else if (nextPhase > state.totalPhases) {
      if (homeScore === awayScore) {
        newGoldenGoal = true;
        newEvents.push(
          buildEvent(state.phase, "kickoff", atkSide, shooterPlayer,
            "Draw! Golden Goal — next goal wins.", "⚡ Golden Goal")
        );
      } else {
        finished = true;
      }
    }

    console.log(`[Phase ${state.phase}] PendingShot resolved → score ${homeScore}–${awayScore}${finished ? " · FINISHED" : ""}`);

    return {
      ...state,
      phase: nextPhase,
      possession: newPossession,
      ball: { side: newPossession, playerId: newBallHolder },
      homeScore,
      awayScore,
      fatigue,
      pendingShot: undefined,
      events: [...newEvents.reverse(), ...state.events],
      finished,
      goldenGoal: newGoldenGoal,
    };
  }

  // ── Normal phase ──────────────────────────────────────────────────────────
  const homeStats = state.homeStats;
  const awayStats = state.awayStats;

  const newEvents: MatchEvent[] = [];
  let homeScore = state.homeScore;
  let awayScore = state.awayScore;
  let possession = state.possession;
  let ballHolderId = state.ball.playerId;

  // ── Step 1: Speed → initiative (who attacks this phase) ─────────────────
  const homeSpd = Math.max(1, homeStats.vitesse);
  const awaySpd = Math.max(1, awayStats.vitesse);
  let homeInitChance: number;
  if (homeSpd >= awaySpd) {
    homeInitChance = SPEED_MAX_CHANCE - (SPEED_MAX_CHANCE - 0.5) * (awaySpd / homeSpd) ** SPEED_EXPONENT;
  } else {
    homeInitChance = SPEED_MIN_CHANCE + (0.5 - SPEED_MIN_CHANCE) * (homeSpd / awaySpd) ** SPEED_EXPONENT;
  }
  const speedRoll = rng();
  const atk: Side = speedRoll < homeInitChance ? "home" : "away";
  const def: Side = other(atk);

  const atkStats  = atk === "home" ? homeStats : awayStats;
  const defStats  = def === "home" ? homeStats : awayStats;
  const atkTeam   = teamOf(state, atk);
  const defTeam   = teamOf(state, def);
  const atkPlayer = pick(atkTeam.players, rng);
  const defPlayer = pick(defTeam.players, rng);

  console.log(
    `[Phase ${state.phase}] Speed: home=${homeSpd} away=${awaySpd}, homeChance=${homeInitChance.toFixed(2)}, roll=${speedRoll.toFixed(3)} → ${atk} attacks`
  );

  possession   = atk;
  ballHolderId = atkPlayer.id;

  // ── Step 2: Defense stop check ───────────────────────────────────────────
  const atkTech = Math.max(1, atkStats.technique);
  const defDef  = Math.max(1, defStats.defense);
  let defStopChance: number;
  if (defDef >= atkTech) {
    defStopChance = DEFENSE_MAX_STOP - (DEFENSE_MAX_STOP - DEFENSE_BASE_STOP) * (atkTech / defDef);
  } else {
    defStopChance = DEFENSE_BASE_STOP * (defDef / atkTech);
  }
  defStopChance = Math.max(DEFENSE_MIN_STOP, Math.min(DEFENSE_MAX_STOP, defStopChance));

  const defRoll        = rng();
  const defenseStopped = defRoll < defStopChance;
  console.log(
    `[Phase ${state.phase}] Defense: def=${defDef} vs tech=${atkTech}, stopChance=${defStopChance.toFixed(2)}, roll=${defRoll.toFixed(3)} → ${defenseStopped ? "STOPPED" : "failed"}`
  );

  if (defenseStopped) {
    // ── Defense wins → interception ────────────────────────────────────
    newEvents.push(
      buildEvent(state.phase, "interception", def, defPlayer,
        `${defPlayer.name} wins the ball back!`, "🛡️ Interception")
    );
    possession   = def;
    ballHolderId = defPlayer.id;

    // ── Technique → counter-attack (max 1 proc per phase) ──────────────
    const defTech       = Math.max(1, defStats.technique);
    const counterChance = computeTechChance(defTech, atkTech);
    const techRoll      = rng();
    console.log(
      `[Phase ${state.phase}] Technique (counter): defTech=${defTech}, chance=${counterChance.toFixed(2)}, roll=${techRoll.toFixed(3)} → ${techRoll < counterChance ? "PROC" : "no proc"}`
    );

    if (techRoll < counterChance) {
      const counterShooter = pick(defTeam.players, rng);
      newEvents.push(
        buildEvent(state.phase, "dribble", def, counterShooter,
          "Critical Interception", "Critical Interception", "critical")
      );

      const fatigue = { ...state.fatigue };
      [atkPlayer.id, defPlayer.id, counterShooter.id].forEach((id) => {
        fatigue[id] = Math.min(100, (fatigue[id] ?? 0) + 6);
      });

      console.log(`[Phase ${state.phase}] Critical Interception — deferring counter shot to next tick`);

      // Return early: Critical event is alone as events[0] → portrait flash fires.
      // Next stepMatch call resolves the counter shot via pendingShot.
      return {
        ...state,
        possession: def,
        ball: { side: def, playerId: counterShooter.id },
        fatigue,
        pendingShot: { atkSide: def, shooter: { id: counterShooter.id, name: counterShooter.name } },
        events: [...newEvents.reverse(), ...state.events],
        finished: false,
      };
    }
    // No counter proc → phase ends here (possession stays with def, no shot).

  } else {
    // ── Defense fails → attack continues ──────────────────────────────

    // ── Technique → creative pass (max 1 proc per phase) ───────────────
    const defTech        = Math.max(1, defStats.technique);
    const creativeChance = computeTechChance(atkTech, defTech);
    const techRoll       = rng();
    console.log(
      `[Phase ${state.phase}] Technique (creative): atkTech=${atkTech}, chance=${creativeChance.toFixed(2)}, roll=${techRoll.toFixed(3)} → ${techRoll < creativeChance ? "PROC" : "no proc"}`
    );

    if (techRoll < creativeChance) {
      const passer        = pick(atkTeam.players, rng);
      const futureShooter = pick(atkTeam.players, rng);
      newEvents.push(
        buildEvent(state.phase, "dribble", atk, passer,
          "Critical Pass", "Critical Pass", "critical")
      );

      const fatigue = { ...state.fatigue };
      [atkPlayer.id, defPlayer.id, passer.id].forEach((id) => {
        fatigue[id] = Math.min(100, (fatigue[id] ?? 0) + 6);
      });

      console.log(`[Phase ${state.phase}] Critical Pass — deferring shot to next tick`);

      // Return early: Critical event is alone as events[0] → portrait flash fires.
      // Next stepMatch call resolves the shot via pendingShot.
      return {
        ...state,
        possession: atk,
        ball: { side: atk, playerId: passer.id },
        fatigue,
        pendingShot: { atkSide: atk, shooter: { id: futureShooter.id, name: futureShooter.name } },
        events: [...newEvents.reverse(), ...state.events],
        finished: false,
      };
    }

    // ── No critical proc → shot runs immediately ────────────────────────
    const shooter    = pick(atkTeam.players, rng);
    const atkPhys    = Math.max(1, atkStats.physique);
    const defDef2    = Math.max(1, defStats.defense);
    const goalChance = computeGoalChance(atkPhys, defDef2);
    const goalRoll   = rng();
    console.log(
      `[Phase ${state.phase}] Shot: phys=${atkPhys} vs def=${defDef2}, goalChance=${goalChance.toFixed(2)}, roll=${goalRoll.toFixed(3)} → ${goalRoll < goalChance ? "GOAL" : "no goal"}`
    );

    newEvents.push(
      buildEvent(state.phase, "shot", atk, shooter,
        `${shooter.name} shoots!`, "🎯 Shot")
    );

    if (goalRoll < goalChance) {
      if (atk === "home") homeScore++; else awayScore++;
      newEvents.push(
        buildEvent(state.phase, "goal", atk, shooter,
          `⚽ GOAL by ${shooter.name}!`, "🥅 GOAL!")
      );
    } else {
      const gk = pick(defTeam.players, rng);
      if (rng() < 0.6) {
        newEvents.push(
          buildEvent(state.phase, "save", def, gk,
            `${gk.name} makes the save.`, "🧤 Save")
        );
      } else {
        newEvents.push(
          buildEvent(state.phase, "miss", atk, shooter,
            `${shooter.name} misses the target.`, "😖 Miss")
        );
      }
    }

    // After shot: possession resets to defender
    possession   = def;
    ballHolderId = pick(defTeam.players, rng).id;
  }

  // ── Fatigue (cosmetic) ────────────────────────────────────────────────────
  const fatigue = { ...state.fatigue };
  [atkPlayer.id, defPlayer.id, ballHolderId].forEach((id) => {
    fatigue[id] = Math.min(100, (fatigue[id] ?? 0) + 6);
  });

  const nextPhase           = state.phase + 1;
  const isGoldenGoal        = state.goldenGoal ?? false;
  const goalScoredThisPhase = homeScore !== state.homeScore || awayScore !== state.awayScore;

  let finished      = false;
  let newGoldenGoal = isGoldenGoal;

  if (isGoldenGoal) {
    finished = goalScoredThisPhase;
  } else if (nextPhase > state.totalPhases) {
    if (homeScore === awayScore) {
      newGoldenGoal = true;
      newEvents.push(
        buildEvent(state.phase, "kickoff", atk, atkPlayer,
          "Draw! Golden Goal — next goal wins.", "⚡ Golden Goal")
      );
    } else {
      finished = true;
    }
  }

  console.log(`[Phase ${state.phase}] Result: ${atk} attacked → score ${homeScore}–${awayScore}${finished ? " · FINISHED" : ""}`);

  return {
    ...state,
    phase: nextPhase,
    possession,
    ball: { side: possession, playerId: ballHolderId },
    homeScore,
    awayScore,
    fatigue,
    events: [...newEvents.reverse(), ...state.events],
    finished,
    goldenGoal: newGoldenGoal,
  };
}

/** Match result from home (player) perspective. Never draw (golden goal). */
export function matchResult(state: MatchState): import("../types/leagueDuel").DuelResult {
  if (state.homeScore > state.awayScore) return "win";
  if (state.homeScore < state.awayScore) return "loss";
  return "loss";
}

// Silence unused-constant warning for TECHNIQUE_BASE_CHANCE (documents intent, not used in formula).
void TECHNIQUE_BASE_CHANCE;
